import { SearchOutlined } from '@ant-design/icons';
import { Button, Drawer, Input, Modal, Radio, Row, Space } from 'antd';
import { useArchiveCompanyGroupMutation } from 'app/api';
import { useCompanySlice } from 'app/containers/Company/slice';
import { ICompanyGroup } from 'app/containers/Company/slice/types';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IOption } from 'types';
import { showArchiveCompanyGroupSuccessNotification } from '../../../utils/notifications';
import CompaniesTable from './parts/CompaniesTable/CompaniesTable';
import CompanyGroupsTable from './parts/CompanyGroupsTable/CompanyGroupsTable';
import CreateOrEditCompanyGroup from './parts/CreateOrEditCompanyGroup/CreateOrEditCompanyGroup';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

type RadioValueType = 'companies' | 'groups' | 'createOrEditGroup';

const CompanyGroupDrawer: FC<IProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const companyActions = useCompanySlice().actions;
  const envId = useSelector(selectSelectedEnvId) as number;

  const [radioValue, setRadioValue] = useState<RadioValueType>('companies');
  const [search, setSearch] = useState('');

  const [
    companyGroupToEdit,
    setCompanyGroupToEdit,
  ] = useState<ICompanyGroup | null>(null);

  const options: IOption<RadioValueType>[] = [
    { label: t('Company.Companies', 'Companies'), value: 'companies' },
    { label: t('Company.CompanyGroups', 'Company groups'), value: 'groups' },
  ];
  const [archiveCompanyGroup] = useArchiveCompanyGroupMutation();

  const getFooter = () => {
    if (radioValue !== 'createOrEditGroup') {
      return null;
    }

    return (
      <Row justify="end">
        <Space>
          <Button onClick={() => setRadioValue('groups')}>
            {t('Common.Cancel', 'Cancel')}
          </Button>

          <Button
            form="createCompanyGroup"
            key="submit"
            htmlType="submit"
            type="primary"
          >
            {t('Common.Save', 'Save')}
          </Button>
        </Space>
      </Row>
    );
  };

  const onCompanyActivate = (clickedCompanyId: number) => {
    dispatch(companyActions.setSelectedCompanyId(clickedCompanyId));
    onClose();
  };

  const onGroupActivate = (groupId: number) => {
    dispatch(companyActions.setSelectedCompanyGroupId(groupId));
    onClose();
  };

  const onGroupArchiveConfirm = async (groupId: number) => {
    try {
      await archiveCompanyGroup({ envId, companyGroupId: groupId }).unwrap();
      dispatch(companyActions.setSelectedCompanyId(null));
      showArchiveCompanyGroupSuccessNotification();
    } catch (error) {
      handleError(error);
    }
  };

  const onGroupArchive = (groupId: number) => {
    Modal.confirm({
      title: t(
        'Company.ArchiveCompanyGroupConfirm',
        'Are you sure you want to archive this company group?',
      ),
      onOk: () => onGroupArchiveConfirm(groupId),
      okText: t('Common.Yes', 'Yes'),
      cancelText: t('Common.No', 'No'),
    });
  };

  const getDrawerTitle = () => {
    if (radioValue === 'companies') {
      return t('Company.Companies', 'Companies');
    }

    if (radioValue === 'groups') {
      return t('Company.CompanyGroups', 'Company groups');
    }

    if (radioValue === 'createOrEditGroup') {
      if (companyGroupToEdit) {
        return t('Company.EditGroup', 'Edit company group');
      }

      return t('Company.CreateNewGroup', 'Create new group');
    }

    return '';
  };

  return (
    <Drawer
      title={getDrawerTitle()}
      visible={isOpen}
      placement="left"
      width={718}
      onClose={onClose}
      footer={getFooter()}
    >
      {(radioValue === 'companies' || radioValue === 'groups') && (
        <Space size="large">
          <Radio.Group
            onChange={e => setRadioValue(e.target.value)}
            value={radioValue}
            options={options}
          />

          <Input
            prefix={<SearchOutlined />}
            placeholder={t('Common.Search', 'Search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Space>
      )}

      {radioValue === 'companies' && (
        <CompaniesTable onCompanyActivate={onCompanyActivate} search={search} />
      )}

      {radioValue === 'groups' && (
        <CompanyGroupsTable
          search={search}
          onGroupActivate={onGroupActivate}
          onGroupArchive={onGroupArchive}
          onGroupEdit={companyGroup => {
            setCompanyGroupToEdit(companyGroup);
            setRadioValue('createOrEditGroup');
          }}
          onCreateNewGroup={() => {
            setCompanyGroupToEdit(null);
            setRadioValue('createOrEditGroup');
          }}
        />
      )}

      {radioValue === 'createOrEditGroup' && (
        <CreateOrEditCompanyGroup
          onBackToGroups={() => setRadioValue('groups')}
          companyGroup={companyGroupToEdit}
        />
      )}
    </Drawer>
  );
};

export default CompanyGroupDrawer;
